import { readGaIds } from '@/lib/ga-client';
import { Subscription, Invoice } from '@/pages/create-report/billing/types';
import { apiClient } from './client';

export interface Plan {
    id: 'BASIC' | 'INTERMEDIATE' | 'PRO';
    price_id: string | null;
    name: string;
    price: number;
    currency: string;
    features: string[];
}

interface CheckoutResponse {
    url: string;
    sessionId: string;
}

interface PortalResponse {
    url: string;
}

type CheckoutContext = 'billing' | 'subscribe';
type TrialBehavior = 'defer_until_opt_in';

type CheckoutInput = {
    planId?: Plan['id'];
    priceId?: string;
    context?: CheckoutContext;
    trialBehavior?: TrialBehavior;
};

export const paymentApi = {
    getPlans: async (): Promise<Plan[]> => {
        const response = await apiClient.get<Plan[]>('/payment/plans');
        return response as unknown as Plan[];
    },
    checkout: async (input: CheckoutInput): Promise<CheckoutResponse> => {
        // Read here rather than at each call site: the identifiers are needed
        // for every checkout, and the conversion is only as good as the last
        // place someone remembered to pass them along. Both are undefined when
        // the visitor blocks analytics, and the API treats them as optional.
        const { clientId, sessionId } = readGaIds();

        const response = await apiClient.post<CheckoutResponse>('/payment/checkout', {
            plan_id: input.planId,
            price_id: input.priceId,
            context: input.context,
            trial_behavior: input.trialBehavior,
            ga_client_id: clientId,
            ga_session_id: sessionId,
        });
        return response as unknown as CheckoutResponse;
    },
    getSubscription: async (): Promise<Subscription> => {
        const response = await apiClient.get<Subscription>('/payment/subscription');
        return response as unknown as Subscription;
    },
    createPortalSession: async (): Promise<PortalResponse> => {
        const response = await apiClient.post<PortalResponse>('/payment/portal');
        return response as unknown as PortalResponse;
    },
    getInvoices: async (): Promise<Invoice[]> => {
        const response = await apiClient.get<Invoice[]>('/payment/invoices');
        return response as unknown as Invoice[];
    },
};
